import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription, fromEvent, merge } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { CacheInterceptor } from 'src/app/utils/cache.interceptor';
import { JWTTokenServiceService } from 'src/app/public/services/token.service';

/** Away this long before data is treated as stale and refreshed. */
const STALE_AFTER_MS = 60_000;
/** Prevent rapid remount loops around focus/visibility chatter. */
const REFRESH_COOLDOWN_MS = 10_000;

/**
 * When the CRM tab/window is hidden for a while (laptop sleep, lock screen,
 * background tab) and the user returns, HTTP list caches can still serve
 * outdated data. This service clears the interceptor cache and soft-reloads
 * the current route so modules refetch fresh data without a full browser
 * refresh or logging the user out.
 */
@Injectable({ providedIn: 'root' })
export class ResumeDataRefreshService implements OnDestroy {
  private hiddenAt: number | null = null;
  private lastActiveAt = Date.now();
  private sub: Subscription | null = null;
  private refreshing = false;
  private lastRefreshAt = 0;

  constructor(
    private cache: CacheInterceptor,
    private router: Router,
    private tokenService: JWTTokenServiceService,
    private ngZone: NgZone,
  ) {}

  start(): void {
    this.stop();
    this.lastActiveAt = Date.now();
    this.hiddenAt = document.hidden ? Date.now() : null;

    this.ngZone.runOutsideAngular(() => {
      this.sub = merge(
        fromEvent(document, 'visibilitychange'),
        fromEvent(window, 'pageshow'),
        fromEvent(window, 'focus'),
        fromEvent(window, 'blur'),
      )
        .pipe(debounceTime(250))
        .subscribe((event) => this.handleResumeSignal(event));
    });
  }

  stop(): void {
    this.sub?.unsubscribe();
    this.sub = null;
    this.hiddenAt = null;
  }

  private handleResumeSignal(event: Event): void {
    if (event.type === 'blur' || document.hidden) {
      if (this.hiddenAt == null) {
        this.hiddenAt = Date.now();
      }
      return;
    }

    // focus / pageshow / visibility → visible
    const pageShow = event as PageTransitionEvent;
    const fromBfCache =
      event.type === 'pageshow' && pageShow.persisted === true;
    const reference = this.hiddenAt ?? this.lastActiveAt;
    const awayMs = Date.now() - reference;
    this.hiddenAt = null;
    this.lastActiveAt = Date.now();

    if (!this.tokenService.getToken()) {
      return;
    }

    if (!fromBfCache && awayMs < STALE_AFTER_MS) {
      return;
    }

    this.ngZone.run(() => this.refreshStaleData());
  }

  private refreshStaleData(): void {
    if (this.refreshing) {
      return;
    }
    if (Date.now() - this.lastRefreshAt < REFRESH_COOLDOWN_MS) {
      return;
    }

    const url = this.router.url || '';
    if (!url || !url.startsWith('/main') || url.includes('/login')) {
      return;
    }

    this.refreshing = true;
    this.lastRefreshAt = Date.now();
    this.cache.clear();

    // Bounce via another authenticated route (not `/` — that redirects to login).
    // skipLocationChange keeps the URL bar unchanged during the remount.
    const bounceUrl =
      url === '/main/leads' || url.startsWith('/main/leads?')
        ? '/main/contacts'
        : '/main/leads';

    this.router
      .navigateByUrl(bounceUrl, { skipLocationChange: true })
      .then(() => this.router.navigateByUrl(url))
      .finally(() => {
        this.refreshing = false;
      });
  }

  ngOnDestroy(): void {
    this.stop();
  }
}
